import axios from "axios";
import React from "react";
import { createRoot } from "react-dom/client";
import ErrorModal from "./ErrorModal";

// Shows the ErrorModal from plain (non-component) code — replaces showErrorModal().
// Mounts the modal on its own, then removes it when the user clicks OK.
const showErrorModal = (msg) => {
  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);
  const close = () => {
    root.unmount();
    host.remove();
  };
  root.render(
    React.createElement(ErrorModal, {
      open: true,
      handleClose: close,
      msg,
      type: "error",
    }),
  );
};

export const viewImage = async (link, storedUser) => {
  try {
    if (link.includes("https")) {
      const url = new URL(link);
      const parts = url.pathname.split("/");
      const blobName = decodeURIComponent(parts.slice(2).join("/"));
      // 1. Call backend to generate SAS URL for this blob
      const res = await axios.get(
        `${
          process.env.REACT_APP_BACKEND_URL
        }/file/generate-blob-sas?blobName=${encodeURIComponent(blobName)}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        }
      );
      const sasUrl = res.data;
      return sasUrl;
    } else {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/file?filename=${link}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
          responseType: "blob", // Important to handle binary data
        }
      );

      // Create a Blob URL
      const fileURL = window.URL.createObjectURL(
        new Blob([response.data], { type: response.headers["content-type"] })
      );

      // Open in a new tab
      // window.open(fileURL, "_blank");
      return fileURL;
    }
  } catch (error) {
    console.error("Error viewing PDF:", error);
    showErrorModal("Could not open PDF.");
  }
};

export const viewImageOnNewTab = async (link, storedUser) => {
  try {
    if (link.includes("https")) {
      const url = new URL(link);
      const parts = url.pathname.split("/");
      const blobName = decodeURIComponent(parts.slice(2).join("/"));
      // 1. Call backend to generate SAS URL for this blob
      const res = await axios.get(
        `${
          process.env.REACT_APP_BACKEND_URL
        }/file/generate-blob-sas?blobName=${encodeURIComponent(blobName)}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        }
      );
      const sasUrl = res.data;
      window.open(sasUrl, "_blank");
      return sasUrl;
    } else {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/file?filename=${link}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
          responseType: "blob", // Important to handle binary data
        }
      );

      // Create a Blob URL
      const fileURL = window.URL.createObjectURL(
        new Blob([response.data], { type: response.headers["content-type"] })
      );

      // Open in a new tab
      window.open(fileURL, "_blank");
      return fileURL;
    }
  } catch (error) {
    console.error("Error viewing PDF:", error);
    showErrorModal("Could not open PDF.");
  }
};

export const viewPDF = async (link, storedUser) => {
  try {
    if (link.includes("https")) {
      const url = new URL(link);
      const parts = url.pathname.split("/");
      const blobName = decodeURIComponent(parts.slice(2).join("/"));
      // 1. Call backend to generate SAS URL for this blob
      const res = await axios.get(
        `${
          process.env.REACT_APP_BACKEND_URL
        }/file/generate-blob-sas?blobName=${encodeURIComponent(blobName)}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
        }
      );
      const sasUrl = res.data;
      // 2. Open in new tab
      window.open(sasUrl, "_blank");
    } else {
      const response = await axios.get(
        `${process.env.REACT_APP_BACKEND_URL}/file?filename=${link}`,
        {
          headers: { Authorization: `Bearer ${storedUser.accessToken}` },
          responseType: "blob",
        }
      );

      const contentType = response.headers["content-type"];
      const blob = new Blob([response.data], { type: contentType });

      // File URL from blob
      const fileURL = URL.createObjectURL(blob);

      // Check supported document types (excluding images)
      const supportedDocumentTypes = [
        "application/pdf",
        "application/msword", // .doc
        "application/vnd.openxmlformats-officedocument.wordprocessingml.document", // .docx
        "application/vnd.ms-powerpoint", // .ppt
        "application/vnd.openxmlformats-officedocument.presentationml.presentation", // .pptx
        "application/vnd.ms-excel", // .xls
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", // .xlsx
        "text/plain",
        "text/html",
        "image/jpeg",
        "image/jpg",
        "image/png",
        "image/webp",
      ];

      if (supportedDocumentTypes.includes(contentType)) {
        window.open(fileURL, "_blank");
      } else {
        showErrorModal("This file type is not supported for in-browser viewing.");
      }
    }
  } catch (error) {
    console.error("Error viewing PDF:", error);
    showErrorModal("Could not open PDF.");
  }
};